import { Injectable, inject, signal, computed, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import {
  EventType, AccountInfo, AuthenticationResult,
} from '@azure/msal-browser';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AUTH_SCOPES } from './auth.config';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly msal      = inject(MsalService);
  private readonly broadcast = inject(MsalBroadcastService);
  private readonly router    = inject(Router);
  private readonly zone      = inject(NgZone);
  private readonly destroy$  = new Subject<void>();

  private readonly _account = signal<AccountInfo | null>(null);
  private readonly _ready   = signal(false);

  readonly account    = this._account.asReadonly();
  readonly ready      = this._ready.asReadonly();
  readonly isLoggedIn = computed(() => this._account() !== null);

  readonly displayName = computed(() =>
    this._account()?.name ?? this._account()?.username ?? 'User'
  );
  readonly userEmail = computed(() => this._account()?.username ?? '');
  readonly userId    = computed(() => this._account()?.localAccountId ?? '');

  initialize(): void {
    this.msal.instance.initialize().then(() => {
      this.msal.instance.handleRedirectPromise().then((result) => {
      if (result?.account) {
        this.zone.run(() => {
          this.setAccount(result.account);
          this.router.navigate(['/dashboard']);
        });
      } else {
        const accounts = this.msal.instance.getAllAccounts();
        if (accounts.length > 0) {
          this.zone.run(() => this.setAccount(accounts[0]));
        }
      }
      this._ready.set(true);
    }).catch(() => {
      this._ready.set(true);
    });

    this.broadcast.msalSubject$.pipe(
      filter((e) => e.eventType === EventType.LOGIN_SUCCESS),
      takeUntil(this.destroy$)
    ).subscribe((e) => {
      const payload = e.payload as AuthenticationResult;
      this.zone.run(() => {
        this.setAccount(payload.account);
        this.router.navigate(['/dashboard']);
      });
    });
  }).catch(() => this._ready.set(true));
  }

  login(): void {
    this.msal.instance.loginRedirect({ scopes: AUTH_SCOPES.api });
  }

  logout(): void {
    this.msal.instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin + '/landing',
    });
  }

  private setAccount(account: AccountInfo | null): void {
    this._account.set(account);
    if (account) this.msal.instance.setActiveAccount(account);
  }

  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}